import React, { useEffect, useRef, useState } from 'react';
import './App.css';
import WhatsAppButton from './components/ui/WhatsAppButton';
import { GALLERY_IMAGES } from './constants/gallery';

const CDN = 'https://cdn.wave4venture.pt/assets/images';
const VIDEO_DIR = '/assets/video';
const WHATSAPP = 'https://wa.me/351910551359?text=Bom%20dia,%0AGostaria%20de%20agendar%20uma%20visita%20%C3%A0s%20moradias.%0A%0ANome:%20';
const EMAIL = 'geral@wave4venture.pt';
const FORMSPREE = 'https://formspree.io/f/mkgjgogv';
const MAP = { lat: 41.236438004927834, lng: -8.211539979585654 };

const STATS = [
  { value: '3+1', label: 'Quartos' },
  { value: '210 m²', label: 'Área interior' },
  { value: 'Privado', label: 'Jardim e terraço' },
  { value: '3 min', label: 'da A4 / A11' },
];

const SPACES = [
  { src: `${CDN}/interior/SALA-OPEN_Scene-1.effectsResult.png`, title: 'Sala open space', text: 'Sala ampla e luminosa, com móvel de TV em ripado de madeira.', size: 'tall' },
  { src: `${CDN}/interior/SALA-OPEN_Scene-6.effectsResult.png`, title: 'Cozinha', text: 'Ilha central em pedra e eletrodomésticos encastrados.', size: '' },
  { src: `${CDN}/interior/SALA-OPEN_Scene-5.effectsResult.png`, title: 'Zona de jantar', text: 'Zona de jantar integrada no open space.', size: '' },
  { src: `${CDN}/interior/suite_Scene-7.effectsResult.png`, title: 'Suite', text: 'Cabeceira estofada e iluminação indireta.', size: 'tall' },
  { src: `${CDN}/interior/suite_Scene-3.effectsResult.png`, title: 'Closet', text: 'Closet em madeira na suite principal.', size: '' },
  { src: `${CDN}/interior/suite_Scene-5.effectsResult.png`, title: 'Casa de banho', text: 'Duche walk-in e lavatório duplo.', size: '' },
  { src: `${CDN}/interior/SALA-OPEN_Scene-9.effectsResult.png`, title: 'Zona de estar', text: 'Sofá em L virado para o móvel de TV com estante iluminada.', size: '' },
];

const PLANS = [
  { id: 'rc', label: 'Rés-do-chão', src: `${CDN}/projeto/PLANTA-DO-R-CHAO.png`, text: 'Sala, cozinha e jantar em open space, com ligação ao jardim.' },
  { id: 'andar', label: 'Andar', src: `${CDN}/projeto/PLANTA-DO-ANDAR.png`, text: 'Suite com closet e casa de banho privada, e dois quartos.' },
  { id: 'lotes', label: 'Lotes', src: `${CDN}/projeto/Lotes.png`, text: 'Distribuição dos 3 lotes, pensada para privacidade e harmonia com o ambiente.' },
];

const DISTANCES = [
  ['2 min', 'Supermercado'],
  ['2 min', 'Escola'],
  ['3 min', 'Autoestrada A4 / A11'],
  ['10 min', 'Centro de Penafiel'],
  ['30 min', 'Porto'],
];

const ALL_IMAGES = [...GALLERY_IMAGES]
  .sort((a, b) => (a.category === b.category ? 0 : a.category === 'exterior' ? -1 : 1))
  .map((img) => ({ src: img.src, alt: img.alt }));

type Lightbox = { images: { src: string; alt: string }[]; index: number } | null;

const App: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [plan, setPlan] = useState(PLANS[0].id);
  const [lightbox, setLightbox] = useState<Lightbox>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Ecrã vertical (telemóvel) usa o filme 9:16; ecrãs pequenos na horizontal usam o 720p
  const [video] = useState(() => {
    if (window.matchMedia('(orientation: portrait)').matches && window.innerWidth <= 900) {
      return { src: `${VIDEO_DIR}/drone-vertical.mp4`, poster: `${VIDEO_DIR}/drone-poster-vertical.jpg` };
    }
    return {
      src: `${VIDEO_DIR}/${window.innerWidth <= 900 ? 'drone-720' : 'drone-1080'}.mp4`,
      poster: `${VIDEO_DIR}/drone-poster.jpg`,
    };
  });

  useEffect(() => {
    document.title = 'wave4venture | Moradias em Penafiel';
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // O React não escreve o atributo `muted` no HTML e o Safari do iPhone só faz autoplay
  // de vídeos sem som; se mesmo assim for bloqueado (ex.: modo de poupança de bateria),
  // arranca no primeiro toque
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');
    const events = ['touchend', 'click', 'scroll'];
    const retry = () => {
      v.play().then(() => events.forEach((ev) => window.removeEventListener(ev, retry))).catch(() => {});
    };
    v.play().catch(() => events.forEach((ev) => window.addEventListener(ev, retry, { passive: true })));
    return () => events.forEach((ev) => window.removeEventListener(ev, retry));
  }, []);

  // Reveal-on-scroll
  useEffect(() => {
    const els = rootRef.current?.querySelectorAll('.nl-reveal') ?? [];
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.15 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Lock scroll + keyboard for overlays
  useEffect(() => {
    const open = !!lightbox || menuOpen;
    document.body.style.overflow = open ? 'hidden' : '';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setLightbox(null); setMenuOpen(false); }
      if (!lightbox) return;
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, menuOpen]);

  const step = (d: number) =>
    setLightbox((lb) => lb && { ...lb, index: (lb.index + d + lb.images.length) % lb.images.length });

  const go = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const activePlan = PLANS.find((p) => p.id === plan)!;

  return (
    <div className="nl" ref={rootRef}>
      {/* Header */}
      <header className={`nl-header ${scrolled ? 'is-solid' : ''}`}>
        <a href="#top" className="nl-logo" onClick={(e) => go(e, 'top')}>
          <img src={`${CDN}/logo.png`} alt="wave4venture" />
        </a>
        <nav className={`nl-nav ${menuOpen ? 'is-open' : ''}`}>
          <a href="#moradia" onClick={(e) => go(e, 'moradia')}>A moradia</a>
          <a href="#interiores" onClick={(e) => go(e, 'interiores')}>Interiores</a>
          <a href="#plantas" onClick={(e) => go(e, 'plantas')}>Plantas</a>
          <a href="#localizacao" onClick={(e) => go(e, 'localizacao')}>Localização</a>
          <a href="#contacto" className="nl-btn nl-btn--small" onClick={(e) => go(e, 'contacto')}>Agendar visita</a>
        </nav>
        <button className={`nl-burger ${menuOpen ? 'is-open' : ''}`} aria-label="Menu" onClick={() => setMenuOpen(!menuOpen)}>
          <span /><span />
        </button>
      </header>

      {/* Hero with drone film */}
      <section className="nl-hero" id="top">
        <video
          ref={videoRef}
          className="nl-hero__video"
          src={video.src}
          poster={video.poster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
        />
        <div className="nl-hero__shade" />
        <div className="nl-hero__content">
          <p className="nl-eyebrow">Penafiel · Porto</p>
          <h1>Moradias contemporâneas<br />com jardim privado</h1>
          <p className="nl-hero__price">desde <strong>415.000€</strong></p>
          <div className="nl-hero__actions">
            <a href="#contacto" className="nl-btn" onClick={(e) => go(e, 'contacto')}>Agendar visita</a>
            <a href="#interiores" className="nl-btn nl-btn--ghost" onClick={(e) => go(e, 'interiores')}>
              Ver fotos
            </a>
          </div>
        </div>
        <a href="#moradia" className="nl-scroll" onClick={(e) => go(e, 'moradia')} aria-label="Descer">
          <span />
        </a>
      </section>

      {/* Stats */}
      <section className="nl-stats">
        {STATS.map((s) => (
          <div className="nl-stat nl-reveal" key={s.label}>
            <span className="nl-stat__value">{s.value}</span>
            <span className="nl-stat__label">{s.label}</span>
          </div>
        ))}
      </section>

      {/* Intro */}
      <section className="nl-intro" id="moradia">
        <div className="nl-intro__text nl-reveal">
          <p className="nl-eyebrow nl-eyebrow--dark">A moradia</p>
          <h2>Uma casa pensada para viver, do jardim à suite.</h2>
          <p>
            Três moradias T3+1 com arquitetura contemporânea, fachadas em ripado de madeira e
            grandes vãos envidraçados. São 210 m² úteis distribuídos por dois pisos, além de
            garagem, varandas, terraços e um jardim privado.
          </p>
          <ul className="nl-checks">
            <li>Suite com closet e casa de banho privada</li>
            <li>2 quartos + escritório</li>
            <li>Garagem, varandas e terraços</li>
            <li>Jardim privado com terraço</li>
          </ul>
        </div>
        <div className="nl-intro__media nl-reveal">
          <img className="nl-intro__main" src={`${CDN}/exterior/6.png`} alt="Fachada da moradia com jardim" loading="lazy" />
          <img className="nl-intro__inset" src={`${CDN}/exterior/1.png`} alt="Vista lateral das moradias" loading="lazy" />
        </div>
      </section>

      {/* Interiors */}
      <section className="nl-interiors" id="interiores">
        <div className="nl-section-head nl-reveal">
          <p className="nl-eyebrow nl-eyebrow--dark">Interiores</p>
          <h2>Espaços amplos, acabamentos quentes</h2>
        </div>
        <div className="nl-bento">
          {SPACES.map((s, i) => (
            <button
              key={s.title}
              className={`nl-tile nl-reveal ${s.size ? `nl-tile--${s.size}` : ''}`}
              style={{ transitionDelay: `${(i % 3) * 80}ms` }}
              onClick={() => setLightbox({ images: SPACES.map((x) => ({ src: x.src, alt: x.title })), index: i })}
            >
              <img src={s.src} alt={s.title} loading="lazy" />
              <span className="nl-tile__caption">
                <strong>{s.title}</strong>
                <em>{s.text}</em>
              </span>
            </button>
          ))}
        </div>
        <div className="nl-center nl-reveal">
          <button className="nl-btn nl-btn--outline" onClick={() => setLightbox({ images: ALL_IMAGES, index: 0 })}>
            Ver todas as imagens ({ALL_IMAGES.length})
          </button>
        </div>
      </section>

      {/* Plans */}
      <section className="nl-plans" id="plantas">
        <div className="nl-section-head nl-reveal">
          <p className="nl-eyebrow nl-eyebrow--dark">Plantas</p>
          <h2>Cada metro quadrado a trabalhar para si</h2>
        </div>
        <div className="nl-tabs nl-reveal" role="tablist">
          {PLANS.map((p) => (
            <button key={p.id} role="tab" aria-selected={plan === p.id} className={plan === p.id ? 'is-active' : ''} onClick={() => setPlan(p.id)}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="nl-plan nl-reveal">
          <button className="nl-plan__img" onClick={() => setLightbox({ images: [{ src: activePlan.src, alt: activePlan.label }], index: 0 })}>
            <img key={activePlan.id} src={activePlan.src} alt={`Planta - ${activePlan.label}`} loading="lazy" />
          </button>
          <div className="nl-plan__text">
            <h3>{activePlan.label}</h3>
            <p>{activePlan.text}</p>
            <p className="nl-muted">
              Plantas completas em alta resolução, caderno de encargos e mapa de acabamentos disponíveis a pedido.
            </p>
            <a href="#contacto" className="nl-link" onClick={(e) => go(e, 'contacto')}>Pedir documentação →</a>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="nl-location" id="localizacao">
        <div className="nl-location__text nl-reveal">
          <p className="nl-eyebrow">Localização</p>
          <h2>Tranquilidade, a minutos de tudo</h2>
          <ul className="nl-distances">
            {DISTANCES.map(([t, place]) => (
              <li key={place}><span>{t}</span>{place}</li>
            ))}
          </ul>
          <a className="nl-btn nl-btn--light" href={`https://maps.google.com/maps?q=${MAP.lat},${MAP.lng}&t=k`} target="_blank" rel="noopener noreferrer">
            Obter direções
          </a>
        </div>
        <div className="nl-location__map nl-reveal">
          <iframe
            title="Localização das moradias"
            src={`https://maps.google.com/maps?q=${MAP.lat},${MAP.lng}&z=15&output=embed&t=k`}
            loading="lazy"
          />
        </div>
      </section>

      {/* Contact */}
      <ContactBlock />

      <footer className="nl-footer">
        <img src={`${CDN}/logo.png`} alt="wave4venture" />
        <p>&copy; {new Date().getFullYear()} wave4venture. Todos os direitos reservados.</p>
        <a href="https://diogo-rocha.com/" target="_blank" rel="noopener noreferrer">Desenvolvido por Diogo Rocha</a>
      </footer>

      <WhatsAppButton />

      {/* Lightbox */}
      {lightbox && (
        <div className="nl-overlay" onClick={() => setLightbox(null)}>
          <button className="nl-overlay__close" aria-label="Fechar">×</button>
          {lightbox.images.length > 1 && (
            <>
              <button className="nl-overlay__nav nl-overlay__nav--prev" aria-label="Anterior" onClick={(e) => { e.stopPropagation(); step(-1); }}>‹</button>
              <button className="nl-overlay__nav nl-overlay__nav--next" aria-label="Seguinte" onClick={(e) => { e.stopPropagation(); step(1); }}>›</button>
            </>
          )}
          <figure onClick={(e) => e.stopPropagation()}>
            <img key={lightbox.index} src={lightbox.images[lightbox.index].src} alt={lightbox.images[lightbox.index].alt} />
            <figcaption>
              {lightbox.images[lightbox.index].alt}
              {lightbox.images.length > 1 && <span>{lightbox.index + 1} / {lightbox.images.length}</span>}
            </figcaption>
          </figure>
        </div>
      )}

    </div>
  );
};

const ContactBlock: React.FC = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(FORMSPREE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...form, _subject: 'Novo contato do site wave4venture' }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus('ok');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="nl-contact" id="contacto">
      <div className="nl-contact__intro nl-reveal">
        <p className="nl-eyebrow nl-eyebrow--dark">Contacto</p>
        <h2>Venha conhecer a sua próxima casa</h2>
        <p>Marque uma visita ou peça a documentação completa.</p>
        <div className="nl-contact__channels">
          <a href={WHATSAPP} target="_blank" rel="noopener noreferrer"><span>WhatsApp</span>+351 910 551 359</a>
          <a href="tel:+351910551359"><span>Telefone</span>+351 910 551 359</a>
          <a href={`mailto:${EMAIL}`}><span>Email</span>{EMAIL}</a>
        </div>
      </div>
      <form className="nl-form nl-reveal" onSubmit={onSubmit}>
        <label>Nome<input name="name" required value={form.name} onChange={onChange} /></label>
        <div className="nl-form__row">
          <label>Email<input type="email" name="email" required value={form.email} onChange={onChange} /></label>
          <label>Telefone<input type="tel" name="phone" required value={form.phone} onChange={onChange} /></label>
        </div>
        <label>Mensagem<textarea name="message" rows={4} required value={form.message} onChange={onChange} placeholder="Gostaria de agendar uma visita…" /></label>
        <button className="nl-btn" type="submit" disabled={status === 'sending'}>
          {status === 'sending' ? 'A enviar…' : 'Enviar pedido'}
        </button>
        {status === 'ok' && <p className="nl-form__ok">Mensagem enviada. Entraremos em contacto brevemente.</p>}
        {status === 'error' && <p className="nl-form__err">Não foi possível enviar. Tente por WhatsApp ou telefone.</p>}
      </form>
    </section>
  );
};

export default App;
